<template>
	<div>
		<tw-button
			class="btn-base btn-indigo w-48"
			size="small"
			variant="primary"
			@click="openDialog">
			{{ isPendingVerification ? "Verify Deposit" : "Pay Now" }}
		</tw-button>

		<tw-modal
			:title="`Pay Invoice #${props?.invoice?.invoiceNumber}`"
			modal-max-width="max-w-screen-md"
			ref="payInvoiceModal"
			:close-modal="cancelUnconfirmedPayment">
			<!-- Stripe Payment Element -->
			<div
				v-show="!needsMicrodepositVerification">
				<div
					v-if="!stripeElementsLoaded"
					class="text-center text-gray-600 py-4">
					Loading payment form...
					<tw-loading />
				</div>
				<div
					v-show="stripeElementsLoaded"
					id="payment-element"
					class="transition-opacity duration-300 mt-6" />
				<div
					class="mt-4"
					v-show="stripeElementsLoaded">
					<v-heading>
						Payment Details
					</v-heading>
					<table class="w-full text-left text-sm/6 whitespace-nowrap">
						<colgroup>
							<col class="w-full">
							<col>
						</colgroup>
						<thead class="border-b border-gray-200 text-gray-900">
							<tr>
								<th
									scope="col"
									class="px-0 py-3 font-semibold">
									Services
								</th>
								<th
									scope="col"
									class="py-3 pr-0 pl-8 text-right font-semibold">
									Price
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(service, index) in servicesWithoutProcessingFees"
								:key="index + '-service'"
								class="border-b border-gray-100">
								<td class="max-w-0 px-0 py-5 align-top">
									<div class="truncate font-medium text-gray-900">
										{{ service.description }}
									</div>
								</td>
								<td class="py-5 pr-0 pl-8 text-right align-top text-gray-700 tabular-nums">
									{{ currencyFormatter.format(service.total) }}
								</td>
							</tr>
						</tbody>
						<tfoot>
							<tr>
								<th
									scope="row"
									class="pt-4 font-semibold text-black sm:hidden"
									:class="{'pb-4': showProcessingFee || !props.invoice.amountPaid}">
									Total
								</th>
								<th
									scope="row"
									colspan="1"
									class="hidden pt-4 text-right font-semibold text-black sm:table-cell"
									:class="{'pb-4': showProcessingFee || !props.invoice.amountPaid}">
									Total
								</th>
								<td
									class="pt-4 pr-0 pl-8 text-right font-semibold text-black tabular-nums"
									:class="[showProcessingFee || !props.invoice.amountPaid ? 'pb-4' : 'pb-0']">
									{{ currencyFormatter.format(+totalServicesWithoutProcessingFees) }}
								</td>
							</tr>
							<template v-if="showProcessingFee">
								<tr>
									<th
										scope="row"
										class="font-semibold text-black sm:hidden"
										:class="{'pb-4': !props.invoice.amountPaid || props.invoice?.tax > 0}">
										Processing Fee
									</th>
									<th
										scope="row"
										colspan="1"
										class="hidden text-right font-semibold text-black sm:table-cell"
										:class="{'pb-4': !props.invoice.amountPaid || props.invoice?.tax > 0}">
										Processing Fee
									</th>
									<td
										class="pr-0 pb-0 pl-8 text-right font-semibold text-black tabular-nums"
										:class="{'pb-4': !props.invoice.amountPaid || props.invoice?.tax > 0}">
										{{ currencyFormatter.format(+invoice.ccFee) }}
									</td>
								</tr>
							</template>
							<tr v-if="props.invoice?.tax > 0">
								<th
									scope="row"
									class="font-semibold text-black sm:hidden"
									:class="{'pb-4': !props.invoice.amountPaid}">
									Tax
								</th>
								<th
									scope="row"
									colspan="1"
									class="hidden text-right font-semibold text-black sm:table-cell"
									:class="{'pb-4': !props.invoice.amountPaid}">
									Tax
								</th>
								<td
									class="pr-0 pl-8 text-right font-semibold text-black tabular-nums"
									:class="{'pb-4': !props.invoice.amountPaid}">
									{{ currencyFormatter.format(+props.invoice?.tax) }}
								</td>
							</tr>
							<tr v-if="props.invoice.amountPaid">
								<th
									scope="row"
									class="pb-4 pt-4 font-semibold text-black sm:hidden">
									Paid
								</th>
								<th
									scope="row"
									colspan="1"
									class="hidden pb-4 pt-4 text-right font-semibold text-black sm:table-cell">
									Paid
								</th>
								<td class="pb-4 pt-4 pr-0 pl-8 text-right font-semibold text-black tabular-nums">
									-{{ currencyFormatter.format(props.invoice.amountPaid) }}
								</td>
							</tr>
							<tr class="border-t border-zinc-100">
								<th
									scope="row"
									class="pt-4 text-lg font-semibold text-gray-900 sm:hidden">
									Amount Due
								</th>
								<th
									scope="row"
									colspan="1"
									class="hidden pt-4 text-right text-lg font-semibold text-gray-900 sm:table-cell">
									Amount Due
								</th>
								<td class="pt-4 pr-0 pb-0 pl-8 text-right text-lg font-semibold text-gray-900 tabular-nums">
									{{ currencyFormatter.format(amountDueAfterProcessingFees) }}
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
				<p
					v-show="stripeElementsLoaded"
					class="my-6 flex justify-end">
					A receipt will be sent to &nbsp;<strong>{{ contactEmail }}</strong>
				</p>
				<div
					class="flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto"
					v-show="stripeElementsLoaded">
					<tw-button
						class="btn-base btn-indigo"
						@click="submitPayment"
						:loading="isSubmitting"
						:disabled="isSubmitting || !stripeElementsLoaded">
						Confirm Payment
					</tw-button>
				</div>
			</div>
			<!-- Pending ACH microdeposit verification -->
			<div
				v-show="needsMicrodepositVerification">
				<div class="mt-6 space-y-4">
					<v-heading>
						Verify Your Bank Account
					</v-heading>
					<p class="text-sm/6">
						We sent a small deposit to your bank account, which can take 1 to 2 business days to
						appear on your statement. Enter the deposit amount to finish paying this invoice.
					</p>
				</div>
				<div class="mt-8 flex flex-col-reverse items-center justify-between gap-3 *:w-full sm:flex-row sm:*:w-auto">
					<tw-button
						class="btn-base btn-white"
						@click="switchPaymentMethod"
						:loading="isSwitchingPaymentMethod"
						:disabled="isSwitchingPaymentMethod">
						Use a Different Payment Method
					</tw-button>

					<tw-button
						class="btn-base btn-indigo"
						@click="openVerificationWindow"
						:disabled="isSwitchingPaymentMethod || !verificationHostedUrl">
						Verify Deposit
					</tw-button>
				</div>
			</div>
		</tw-modal>

		<v-alert
			:open="alertIsOpen"
			:on-close="closeAlert"
			static
			size="lg">
			<v-alert-title>
				Payment Submitted
			</v-alert-title>
			<v-alert-body>
				<div class="space-y-4">
					<v-text class="!text-base">
						<v-strong>
							A receipt will be sent to <span class="!font-bold">{{ contactEmail }}</span> when your payment has been processed.
						</v-strong>
					</v-text>
					<v-text
						v-if="selectedPaymentMethod === 'us_bank_account'"
						class="!text-base text-center">
						<v-strong class="!font-bold">
							This will take up to 4 business days for ACH payments.
						</v-strong>
					</v-text>
				</div>
			</v-alert-body>
			<v-alert-actions>
				<tw-button
					class="btn-base btn-indigo"
					@click="closeAlert()">
					Okay
				</tw-button>
			</v-alert-actions>
		</v-alert>
	</div>
</template>

<script setup lang="ts">
	import {
		nextTick,
		ref,
		computed,
	} from 'vue'

	import {
		loadStripe,
	} from '@stripe/stripe-js/pure'

	import {
		Invoice
	} from '@/types'
	
	import {
		store as _userStore
	} from '@/store/user'

	import api from '@/utilities/api'

	import {
		useBrand
	} from '@/utilities/brand'

	const brand = useBrand()

	const props = defineProps({
		invoice: {
			type: Object,
			default: () => ({}) as Invoice,
		},
	})

	const currencyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	})


	const payInvoiceModal = ref<{ open: boolean }>({ open: false })
	const stripe = ref<any>(null)
	const elements = ref<any>(null)
	const clientSecret = ref('')
	const financialConnectionsClientSecret = ref('')
	const customerSessionClientSecret = ref('')
	const isSubmitting = ref(false)
	const stripeElementsLoaded = ref(false)
	const store = _userStore()
	const contactId = ref(store.user.cognitoUser.attributes["custom:fm_contact_id"])
	const stripePromise = loadStripe(process.env.VUE_APP_STRIPE_API_KEY || "")
	const contactEmail = ref(store.user.cognitoUser.attributes.email)
	const needsMicrodepositVerification = ref(false)
	const verificationHostedUrl = ref("")
	const isSwitchingPaymentMethod = ref(false)
	const verificationWasCanceled = ref(false)

	const emit = defineEmits([
		"submitted",
		"verification-canceled",
	])

	// the parent clears paymentStatus on the row when a verification is canceled, so reading the
	// prop stays accurate without a local override
	const isPendingVerification = computed(() => {
		return props.invoice?.paymentStatus === "pending_verification"
	})

	const currentPaymentIntentId = computed(() => clientSecret.value.split("_secret")[0])

	// determines whether a retrieved PaymentIntent is parked on a microdeposit verification.
	// next_action is checked rather than status, since Stripe clears next_action once the intent is
	// canceled or confirmed and the status name for this state varies by API version
	function isAwaitingMicrodepositVerification(paymentIntent: any) {
		return paymentIntent?.next_action?.type === "verify_with_microdeposits"
	}

	async function openDialog() {
		payInvoiceModal.value.open = true
		await createPaymentIntent()
	}

	async function createPaymentIntent() {
		try {
			stripeElementsLoaded.value = false
			needsMicrodepositVerification.value = false

			const body = {
				amount: props?.invoice?.amountDue,
				currency: 'usd',
				filemakerId: contactId.value,
				invoiceId: props?.invoice?.id,
				invoiceNumber: props?.invoice?.invoiceNumber,
			}

			const response = await api("post", "fmDataAPI", "/payment-intent", body)

			if (!response.clientSecret) {
				throw new Error('Invalid PaymentIntent response')
			}

			clientSecret.value = response.clientSecret
			financialConnectionsClientSecret.value = response.financialConnectionsClientSecret
			customerSessionClientSecret.value = response.customerSessionClientSecret
			await mountStripeElements()
			stripeElementsLoaded.value = true
		}
		catch (error) {
			console.error('Error creating PaymentIntent:', error)
			alert('Unable to initiate payment. Please try again.')
		}
	}

	const selectedPaymentMethod = ref("")

	async function mountStripeElements() {
		const elementsOptions = {
			clientSecret: clientSecret.value,
			financialConnectionsClientSecret: financialConnectionsClientSecret.value,
			customerSessionClientSecret: customerSessionClientSecret.value,
			appearance: {
				theme: 'flat',
				variables: {
					fontWeightNormal: '500',
					colorPrimary: brand.stripeColorPrimary,
					borderRadius: '6px',
					tabSpacing: `8px`,
					gridColumnSpacing: `16px`,
					gridRowSpacing: `24px`,
				},
				rules: {
					'.Input': {
						backgroundColor: '#fff',
						border: '1px solid rgba(9,9,11,.1)'
					},
					'.CheckboxInput': {
						backgroundColor: '#fff',
						border: '1px solid rgba(9,9,11,.1)'
					},
				}
			},
		}
		const options = {
			layout: {
				type: 'tabs',
				defaultCollapsed: false,
			},
			fields: {
				billingDetails: {
					email: "never",
				}
			}
		}
		stripe.value = await stripePromise

		// check for existing payment intent
		// show the verification panel instead of the payment form if microdeposits are still required
		const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

		if (isAwaitingMicrodepositVerification(paymentIntent)) {
			verificationHostedUrl.value = paymentIntent.next_action.verify_with_microdeposits?.hosted_verification_url || ""
			needsMicrodepositVerification.value = true
			stripeElementsLoaded.value = true

			return
		}

		// else create new intent
		const elementsInstance = stripe.value.elements(elementsOptions)
		const paymentElement = elementsInstance.create('payment', options)
		paymentElement.mount('#payment-element')
		elements.value = elementsInstance

		paymentElement.on('change', async function(event: any) {
			await nextTick()
			selectedPaymentMethod.value = event.value.payment_method?.type || event.value.type
		})
	}

	async function submitPayment() {
		if (!stripe.value || !elements.value) return
		isSubmitting.value = true

		// update invoice in FIleMaker to ensure processing fee line items is propely applied or removed based on selected payment method
		let updatedInvoice: Invoice
		try {
			updatedInvoice = await api("post", "fmDataAPI", `/fmDataAPI`, {
				action: "updateInvoice",
				payload: {
					ccFee: selectedPaymentMethod.value === 'card' ? props.invoice.ccFee : 0,
					id: props.invoice.id,
				},
			})

			// alert user if updated invoice total + fees is different than what they are about to pay
			if (Math.round(amountDueAfterProcessingFees.value * 100) / 100 !== updatedInvoice.amountDue) {
				alert('Unable to validate invoice amount. Please try again.')
				isSubmitting.value = false
				return
			}
		}
		catch(error: any) {
			console.error(error)
			alert('Unable to validate invoice amount. Please try again.')
			isSubmitting.value = false
			return
		}

		// update payment intent to ensure amount is correct in case user made changes to invoice or there are processing fees that need to be added
		try {
			await api("patch", "fmDataAPI", `/payment-intent/${currentPaymentIntentId.value}`, {
				amount: updatedInvoice.amountDue,
			})
		}
		catch(error: any) {
			console.error("Error updating payment intent:", error)
			alert('Unable to update payment details. Please try again.')
			isSubmitting.value = false
			return
		}

		const { error, paymentIntent } = await stripe.value.confirmPayment({
			elements: elements.value,
			redirect: 'if_required',
			confirmParams: {
				payment_method_data: {
					billing_details: {
						email: contactEmail.value,
					},
				},
			},
		})

		if (error) {
			console.error(error.message)
			alert(error.message)
		}
		else if (isAwaitingMicrodepositVerification(paymentIntent)) {
			// keep the modal open on the verification panel so the customer can still back out
			verificationHostedUrl.value = paymentIntent.next_action.verify_with_microdeposits?.hosted_verification_url || ""
			needsMicrodepositVerification.value = true
			verificationWasCanceled.value = false

			openVerificationWindow()
		}
		else if (paymentIntent?.status === 'processing') {
			payInvoiceModal.value.open = false
			setIsOpen(true)
		}
		else if (paymentIntent?.status === 'succeeded') {
			payInvoiceModal.value.open = false
			setIsOpen(true)
		}

		isSubmitting.value = false
	}

	const showProcessingFee = computed(() => {
		return selectedPaymentMethod.value === 'card' && (props.invoice.ccFee > 0) && (props.invoice.amountDue > 0)
	})

	// cancels the PaymentIntent currently held in clientSecret
	async function cancelCurrentPaymentIntent() {
		const body = {
			paymentIntentId: currentPaymentIntentId.value
		}

		await api("delete", "fmDataAPI", "/payment-intent", body)
	}

	// cancels the intent behind a closed modal. a pending verification is left alone so the customer
	// can still finish it later, and the live status is checked rather than
	// needsMicrodepositVerification, which is still false while the form is loading
	async function cancelUnconfirmedPayment() {
		try {
			const { paymentIntent } = await stripe.value.retrievePaymentIntent(clientSecret.value)

			if (!isAwaitingMicrodepositVerification(paymentIntent)) {
				await cancelCurrentPaymentIntent()
			}
		}
		catch(error: any) {
			console.error(error)
		}

		// refetch on close so the cleared verification badge is reflected in the invoice list
		if (verificationWasCanceled.value) {
			emit("submitted")
		}
	}

	// opens the Stripe hosted page where the customer enters the microdeposit amount. clicking through
	// commits them to ACH, so the modal closes rather than offering the alternate method behind it
	function openVerificationWindow() {
		if (!verificationHostedUrl.value) return

		openCenteredWindow(verificationHostedUrl.value, "_blank", 500, 600)
		payInvoiceModal.value.open = false
	}

	// abandons the pending microdeposit verification and reloads the payment form so the
	// customer can choose a different method
	async function switchPaymentMethod() {
		isSwitchingPaymentMethod.value = true

		try {
			await cancelCurrentPaymentIntent()
		}
		catch(error: any) {
			console.error("Error canceling pending verification:", error)
			alert("Unable to cancel the pending verification. Please try again.")
			isSwitchingPaymentMethod.value = false
			return
		}

		verificationWasCanceled.value = true
		verificationHostedUrl.value = ""

		// FileMaker clears its own status from the payment_intent.canceled webhook, so tell the
		// parent to clear the row now rather than leaving a stale badge until the next fetch
		emit("verification-canceled")
		await createPaymentIntent()

		isSwitchingPaymentMethod.value = false
	}

	const servicesWithoutProcessingFees = computed(() => {
		return props.invoice.services.filter((s: {description: string, total: number}) => {
			return !s.description.toLowerCase().includes('processing fee')
		})
	})

	const totalServicesWithoutProcessingFees = computed(() => {
		return servicesWithoutProcessingFees.value.reduce((total: number, service: {total: number}) => {
			return total + service.total
		}, 0)
	})

	const amountDueAfterProcessingFees = computed(() => {
		return +totalServicesWithoutProcessingFees.value + ( selectedPaymentMethod.value === 'card' ? props.invoice.ccFee : 0 ) + +(props.invoice?.tax || 0) - +props.invoice.amountPaid
	})

	// opens a popup window centered on the user's screen
	function openCenteredWindow(url: string, name: string, width: number, height: number) {
		const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2)
		const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2)

		window.open(url, name, `width=${width},height=${height},left=${left},top=${top}`)
	}

	const alertIsOpen = ref(false)
	function setIsOpen(value: boolean = false) {
		alertIsOpen.value = value
	}

	function closeAlert() {
		alertIsOpen.value = false
		emit("submitted")
	}
</script>
